import type { Dispatch, SetStateAction } from "react"

import type { FormikProps } from "formik"

import type { Prettify } from "../../types/custom/core"
import type { PatchedScriptUpdateRequest } from "../../types/generated/api/PatchedScriptUpdateRequest"
import type { ScriptCreateFromTemplateRequest } from "../../types/generated/api/ScriptCreateFromTemplateRequest"
import type { ScriptCreateRequest } from "../../types/generated/api/ScriptCreateRequest"
import type { ScriptGroupList } from "../../types/generated/api/ScriptGroupList"
import type { ScriptGroupRetrieve } from "../../types/generated/api/ScriptGroupRetrieve"
import type { ScriptSchema } from "../../types/generated/api/ScriptSchema"
import type { ScriptSchemaFeature } from "../../types/generated/api/ScriptSchemaFeature"
import type { ScriptTemplate } from "../../types/generated/api/ScriptTemplate"
import type { ScriptTemplateRetrieve } from "../../types/generated/api/ScriptTemplateRetrieve"
import type { FeatureMapping, SchemaFeature } from "../Workflows"
import type { FeatureMappingFormikValues, Features } from "../Workflows/types/interfaces"

export enum ScriptTab {
  Configuration = "configuration",
  TestFeatures = "testFeatures",
  ScriptCode = "scriptCode",
  Logs = "logs",
  RequirementsPackages = "requirementsPackages",
  // Used in Built-in scripts
  ScriptSetup = "scriptSetup",
  Testing = "testing",
}

export interface MenuOption {
  title: string
  variant?: "negative" | "gray"
  onClick: () => void
}

export interface ScriptCardProps {
  script: ScriptGroupList
  MenuOptions?: MenuOption[]
  onLogsDialogClick: () => void
  onNameClick: () => void
}

export enum StatusColorMap {
  PENDING = "blue",
  RUNNING = "yellow",
  SUCCEEDED = "green",
  FAILED = "red",
  CANCELLED = "red",
}

export enum StatusTextMap {
  PENDING = "Pending",
  RUNNING = "Building",
  SUCCEEDED = "Succeeded",
  FAILED = "Failed",
  CANCELLED = "Cancelled",
}

export interface BuiltinCreationStepProps {
  onTemplateSelect: (templateId: string) => void
  onClose: () => void
  onUseTemplateClick: () => void
  selectedTemplate: string
  scripts?: ScriptGroupList[]
}

export interface LogsDialog {
  isOpen: boolean
  onClose: () => void
  uuid: string
}

export interface TemplateCardProps {
  description?: string
  isInUse?: boolean
  name: string
  isSelected: boolean
  onSelect: () => void
}

export interface SingleScriptProps {
  onClose?: () => void
  scriptData?: ScriptGroupRetrieve
  isScriptLoading?: boolean
  isWorkFlowInReadMode?: boolean | null
  isNewScriptTriggered?: boolean
  isDuplicate?: boolean
  renderView?: "workflow" | "scripts"
  openInEditMode?: boolean
  tab?: ScriptTab
  isVersionDialog?: boolean
  setIsVersionDialog?: () => void
  shouldShowBuildingState?: boolean
  metaData?: ScriptGroupList
}

export interface ScriptDialogProps
  extends Omit<SingleScriptProps, "isVersionDialog" | "scriptData" | "setIsVersionDialog" | "isScriptLoading"> {
  isOpen: boolean
  scriptId?: string
  metaData?: ScriptGroupList
}

export interface ScriptEditorProps {
  isLoading: boolean
  isRefetchingFile: boolean
  script?: ScriptGroupRetrieve
  isReadMode: boolean
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>
  consoleLogs: string[]
  isFetchingFileError: boolean
  logs: string[]
  isConsoleExpanded: boolean
  setIsConsoleExpanded: () => void
  setIsLogsExpanded: () => void
  isLogsExpanded: boolean
  isActionButtonDisabled: boolean
  onActionButtonClick: () => void
  isBuildingState?: boolean
  canShowDetails?: boolean
}

export interface RequirementsDataFile {
  label?: string
  uuid?: string
  createdAt?: string
  value?: string
  // to handle selection of requirement file in grouped select with search
  index?: number | null
  ref?: (node?: Element | null) => void
}

export interface RequirementsListProps {
  requirements: RequirementsDataFile[]
  setRequirementsFile: (file: RequirementsDataFile) => void
  requirementsFile: RequirementsDataFile
  isLoading: boolean
}

export type CredentialsType = {
  key: string
  value: string
  isUpdated: boolean
  isNew: boolean
}

export type CredentialsRequestType = {
  creds: Record<string, string>
  deleted: string[]
}

export type ScriptDialogFormik = {
  snippet: string
  scriptName: string
  isDraft: boolean
  showDraftBtn: boolean
  testFeatures: Record<string, ScriptTestFeature>
  creds: CredentialsType[]
  deletedCred: boolean
  id: string
  requirements: string
  requirementsFileName?: string
}

export type DeleteScriptCredsRequest = {
  project_uuid: string
  script_uuid: string
  creds_keys: string[]
}

export type CreateScriptRequest = ScriptCreateRequest & { project_uuid: string }

export type UpdateScriptRequest = PatchedScriptUpdateRequest & {
  project_uuid: string
  script_uuid: string
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  creds?: Record<string, any>
}

export type TestScriptRequest = { project_uuid: string; script_uuid: string; features?: Features; version?: string }

export type CreateBuiltinScriptRequest = ScriptCreateFromTemplateRequest & {
  project_uuid: string
  template_uuid: string
}

export type CancelBuildingScriptRequest = { project_uuid: string; script_uuid: string; version: string }

export interface CustomScriptTemplate extends ScriptTemplate {
  isInUse?: boolean
}

export type ScriptSetupProps = {
  template: {
    templateData?: ScriptTemplateRetrieve
    isTemplateDataLoading: boolean
    isTemplateDataFetching: boolean
    isFetchingTemplateDataError: boolean
    refetchTemplate: () => Promise<unknown>
  }
  script: {
    scriptData?: ScriptGroupRetrieve
    isScriptDataLoading: boolean
  }

  isScriptInViewMode: boolean
  workflowSchema?: SchemaFeature[]
  workflowFormik?: FormikProps<FeatureMappingFormikValues>
  formik: FormikProps<InitialFormState>
}

export type BuiltinScriptTestingProps = {
  handleRunTestClicked: () => Promise<void>
  isLoading?: boolean
  testingLogsProps: {
    logs?: string[]
    consoleLogs?: string[]
    isTestScriptMutationLoading: boolean
    isConsoleExpanded: boolean
    isLogsExpanded: boolean
    setIsLogsExpanded: Dispatch<SetStateAction<boolean>>
    setIsConsoleExpanded: Dispatch<SetStateAction<boolean>>
  }
}

export type BuiltinScriptDialogProps = {
  isOpen: boolean
  onClose: (value?: string) => void
  scriptInfo: { name: string; uuid: string; scriptId?: string; logo?: string }
  canShowDetails?: boolean
  isNewScriptTriggered?: boolean
  renderView?: "workflow" | "scripts"
  openInEditMode?: boolean
  workflowSchema?: Array<SchemaFeature>
  workflowFormik?: FormikProps<FeatureMappingFormikValues>
  tab?: ScriptTab
  setFormikScriptData?: (scriptSchema: ScriptSchema) => void
}

// Type definition for the initial form state of the script.
export type BaseFormState = {
  /** Name of the script */
  scriptName: string
  /** UUID of the template being used */
  templateId: string
}

export type ScriptTestFeature = Prettify<{ id: string; value: string; original: boolean } & ScriptSchemaFeature>

export interface InitialFormState extends BaseFormState, DataRecordForm {
  /**  Optional array of feature mappings */
  feature_mappings?: Array<FeatureMapping>
  /**  A snapshot of the form state that is of the same type as BaseFormState */

  testFeatures: Record<string, ScriptTestFeature>
}

export interface Cred {
  feat: string
  value: string
  type: string
  isRequired: boolean
}

export interface DataRecordForm {
  creds: Array<Cred>
  feature_mappings?: Array<FeatureMapping>
}

export type ScriptTestingLogsProps = {
  logs?: string[]
  consoleLogs?: string[]
  isTestScriptMutationLoading: boolean
  isConsoleExpanded: boolean
  isLogsExpanded: boolean
  setIsLogsExpanded: Dispatch<SetStateAction<boolean>>
  setIsConsoleExpanded: Dispatch<SetStateAction<boolean>>
  canShowDetails?: boolean
}

export interface CustomScriptTestingProps {
  handleRunTest: () => void
  isLoading: boolean
}
